/**
 * STORE → CHOREOGRAPHER (B4). The reference wiring for the bridge (I1 may use it as is, or copy it).
 *
 *   const unbind = bindChoreographer(choreo);   // after createChoreographer; call unbind() on unmount
 *
 * | store change                         | call                                                       |
 * |--------------------------------------|------------------------------------------------------------|
 * | selectedId: null → id, or A → B      | choreo.select(id, { source: selectSource })                |
 * | selectedId: id → null                | choreo.clear(): home, or the bloom overview during a bloom |
 * | bloomCategory: → cat                 | choreo.bloom(cat); a selection open at that moment is      |
 * |                                      | closed in the store (select(null)); clear() then no-ops    |
 * | bloomCategory: → null                | choreo.clearBloom()                                        |
 * | hoveredId                            | choreo.preview(id) (not during a drive)                    |
 * | phase → "drive"                      | choreo.drive(selectedId, { signal }); when it resolves the |
 * |                                      | phase goes back to "explore" (actions.stopDrive())         |
 * | phase leaves "drive" (End, Esc)      | the drive's signal aborts: "cancelled", camera eases back  |
 *
 * Mood, layers, layout and insets are followed by the controller itself (B2). The intro and the kiosk
 * attract loop call the choreographer directly (B6).
 */
import type { Choreographer } from "@/contracts/map";
import type { AppState } from "@/contracts/store";
import { actions, appStore } from "@/store/store";

export function bindChoreographer(choreo: Choreographer, store: { getState(): AppState; subscribe(fn: (s: AppState, p: AppState) => void): () => void } = appStore): () => void {
  let driveAc: AbortController | null = null;
  let alive = true;

  const startDrive = (id: string) => {
    driveAc?.abort();
    const ac = (driveAc = new AbortController());
    void choreo.drive(id, { signal: ac.signal }).then(() => {
      if (!alive || driveAc !== ac) return;
      driveAc = null;
      if (store.getState().phase === "drive") actions.stopDrive();
    });
  };

  const unsub = store.subscribe((s, p) => {
    if (!alive) return;
    // bloom first: a chip pressed with a card open closes the card (the bloom frames the camera)
    if (s.bloomCategory !== p.bloomCategory) {
      if (s.bloomCategory) {
        void choreo.bloom(s.bloomCategory);
        if (s.selectedId && s.selectedId === p.selectedId) {
          actions.select(null);
          return; // the nested update (selectedId → null) calls clear(), a no-op now
        }
      } else void choreo.clearBloom();
    }
    if (s.selectedId !== p.selectedId) {
      if (s.selectedId) void choreo.select(s.selectedId, { source: s.selectSource ?? undefined });
      else void choreo.clear(); // home, or back to the bloom overview when a bloom is on
    }
    if (s.hoveredId !== p.hoveredId && s.phase !== "drive") choreo.preview(s.hoveredId);
    if (s.phase !== p.phase) {
      if (s.phase === "drive" && s.selectedId) startDrive(s.selectedId);
      else if (p.phase === "drive" && driveAc) {
        const ac = driveAc;
        driveAc = null;
        ac.abort();
      }
    }
  });

  return () => {
    alive = false;
    unsub();
    driveAc?.abort();
    driveAc = null;
  };
}
